import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Box, Grid, Stack } from '@mui/material';
import CalculatorCard from 'lib/designSystem/CalculatorCard';
import TableCard from 'lib/designSystem/Card/TableCard';
import StatCard from 'lib/designSystem/Charts/StatCard';
import PageHeader from 'lib/designSystem/PageHeader';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useMemo } from 'react';

import type { ExtensionDocument } from '../../../../types';

export interface ExtensionSummaryProps {
  extensions: ExtensionDocument[];
  noIvaComision: number | null;
}

const ExtensionSummary: React.FC<ExtensionSummaryProps> = ({
  extensions,
  noIvaComision,
}) => {
  const totals = useMemo(() => {
    const totalAmountDocuments = extensions.reduce(
      (sum, ext) => sum + (ext.amount ?? 0),
      0,
    );
    const totalRemainingDebt = extensions.reduce(
      (sum, ext) => sum + (ext.documentRemainingAmountNet ?? 0),
      0,
    );
    const totalArrear = extensions.reduce(
      (sum, ext) => sum + (ext.totalArrear ?? 0),
      0,
    );

    const totalRateCost = extensions.reduce(
      (sum, ext) => sum + (ext.rateCost ?? 0),
      0,
    );

    const totalOperationCostBase = extensions.reduce(
      (sum, ext) =>
        sum + (ext.operationCostFixed ?? 0) + (ext.operationCostVariable ?? 0),
      0,
    );

    const IVA_FACTOR = noIvaComision ? 1 : 1.19;

    return {
      totalAmountDocuments,
      totalRemainingDebt,
      totalArrear,
      totalRateCost,
      totalOperationCost: totalOperationCostBase * IVA_FACTOR,
    };
  }, [extensions, noIvaComision]);

  const totalGeneral = totals.totalRateCost + totals.totalOperationCost;

  if (extensions.length === 0) return null;

  const operationLabel = `Costo Operación ${noIvaComision ? '' : '+ IVA'}`;
  const subtitle = `${extensions.length} documento${
    extensions.length > 1 ? 's' : ''
  } seleccionado${extensions.length > 1 ? 's' : ''}`;

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Stack direction="column" spacing={2} sx={{ height: '100%' }}>
            <Box alignItems="center">
              <PageHeader variant="dialogHeader">
                <PageHeader.Main>
                  <PageHeader.TitleSection
                    iconType="parameters"
                    title="Configurar parámetros"
                    subtitle={subtitle}
                  />
                </PageHeader.Main>
              </PageHeader>

              <Grid container spacing={0.5}>
                <Grid item xs={12} sm={4} sx={{ flex: 1 }}>
                  <StatCard
                    label="Total Documentos"
                    value={formatMoney(totals.totalAmountDocuments)}
                    valueLabel="CLP"
                    color={BASE_COLORS.blue}
                    icon={<AttachMoneyIcon sx={{ fontSize: 22 }} />}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ flex: 1 }}>
                  <StatCard
                    label="Saldo Deuda"
                    value={formatMoney(totals.totalRemainingDebt)}
                    valueLabel="CLP"
                    color={BASE_COLORS.red}
                    icon={<ErrorOutlineIcon sx={{ fontSize: 22 }} />}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ flex: 1 }}>
                  <StatCard
                    label="Total Atrasos"
                    value={formatMoney(totals.totalArrear)}
                    valueLabel="CLP"
                    color={BASE_COLORS.orange}
                    icon={<ScheduleIcon sx={{ fontSize: 22 }} />}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <TableCard
            size="small"
            enabledEffects
            sx={{
              height: '100%',
              boxShadow: 'none',
              background: 'transparent',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CalculatorCard
              showHeader={false}
              rows={[
                {
                  label: 'Costo tasa',
                  value: totals.totalRateCost,
                },
                {
                  label: operationLabel,
                  value: totals.totalOperationCost,
                },
                {
                  label: 'Total',
                  value: totalGeneral,
                },
              ]}
              highlightLastRows={1}
            />
          </TableCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExtensionSummary;
